import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  House, Package, Receipt, MapPin, Gear, Storefront, SignOut,
  DotsThree, Tag, Crown, Lightning, UserCircle, Clock, ShoppingBag,
  ShieldCheck, ArrowLeft,
} from "@phosphor-icons/react";

const items = [
  { to: "/admin", icon: House, label: "Dashboard", end: true },
  { to: "/admin/orders", icon: Receipt, label: "Orders" },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/categories", icon: Tag, label: "Categories" },
  { to: "/admin/pincodes", icon: MapPin, label: "Pincodes" },
  { to: "/admin/availability", icon: Clock, label: "Availability" },
  { to: "/admin/subscription", icon: Crown, label: "Subscription" },
  { to: "/admin/settings", icon: Gear, label: "Settings" },
  { to: "/admin/profile", icon: UserCircle, label: "Profile" },
];

// 4 quick-access tabs on mobile + a "More" sheet for everything else.
const mobileItems = [
  items[0], // Dashboard
  items[1], // Orders
  items[2], // Products
  items[4], // Pincodes
];
// Anything not in mobileItems above shows up in the "More" sheet.
const mobileMoreLabels = new Set(["Categories", "Availability", "Subscription", "Settings", "Profile"]);

export default function AdminLayout() {
  const { user, logout, switchRole, hasRole } = useAuth();
  const nav = useNavigate();
  const storeSlug = user?.store?.slug || null;
  const [moreOpen, setMoreOpen] = useState(false);

  // Super-admin impersonation state — driven by sessionStorage flag set from
  // SuperStores click-through. When present we show a banner and fetch the
  // impersonated store name for display.
  const impersonateId = typeof window !== "undefined" ? sessionStorage.getItem("qd_impersonate_store") : null;
  const [impersonatedStoreName, setImpersonatedStoreName] = useState("");
  useEffect(() => {
    if (!impersonateId) return;
    api.get(`/admin/stores/${impersonateId}/summary`)
      .then((r) => setImpersonatedStoreName(r.data?.name || ""))
      .catch(() => {});
  }, [impersonateId]);
  const exitImpersonation = () => {
    sessionStorage.removeItem("qd_impersonate_store");
    nav("/super-admin/stores");
  };

  const shopAsCustomer = async () => {
    try {
      // If they don't have customer role yet, just go to /home — backend will
      // add the role automatically next time they log in via customer portal,
      // and /home itself works for any active role. But to keep guarded routes
      // (e.g. /account/addresses) usable, switch active_role.
      if (hasRole("customer")) {
        await switchRole("customer");
      }
      nav("/home");
    } catch {
      toast.error("Couldn't switch. Try logging in as customer.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Super-admin impersonation banner — shown ONLY when acting on behalf of a seller */}
      {impersonateId && (
        <div className="sticky top-0 z-50 bg-amber-500 text-black px-3 sm:px-6 py-2 flex items-center justify-between gap-3 shadow-md" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck weight="fill" size={18} className="shrink-0" />
            <span className="text-xs sm:text-sm font-heading font-black uppercase tracking-wider truncate">
              Admin viewing seller dashboard{impersonatedStoreName ? `: ${impersonatedStoreName}` : ""}
            </span>
          </div>
          <button
            onClick={exitImpersonation}
            className="inline-flex items-center gap-1.5 bg-black text-white text-xs sm:text-sm font-bold px-3 py-1.5 rounded-md hover:bg-gray-800 shrink-0"
            data-testid="exit-impersonation-btn">
            <ArrowLeft size={14} weight="bold" /> Back to Super Admin
          </button>
        </div>
      )}
      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col w-64 min-h-screen border-r border-gray-100 bg-white sticky top-0">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-[#F8CB46] flex items-center justify-center">
                <Lightning weight="fill" size={20} className="text-black" />
              </div>
              <div>
                <div className="font-heading font-black tracking-tight">QuickDukaan</div>
                <div className="text-xs text-gray-500">Shopkeeper</div>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-3 space-y-1">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end}
                data-testid={`admin-nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition ${
                    isActive ? "bg-[#F8CB46]/20 text-black" : "text-gray-600 hover:bg-gray-50"
                  }`
                }>
                <it.icon weight="duotone" size={20} />
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-gray-100 space-y-2">
            {storeSlug ? (
              <a href={`/${storeSlug}`} target="_blank" rel="noopener noreferrer"
                data-testid="admin-view-storefront"
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">
                <Storefront weight="duotone" size={20} /> View storefront
                <span className="ml-auto text-[10px] font-mono text-gray-400 truncate">/{storeSlug}</span>
              </a>
            ) : (
              <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-300 cursor-not-allowed"
                data-testid="admin-view-storefront-disabled">
                <Storefront weight="duotone" size={20} /> No store yet
              </div>
            )}
            <button onClick={shopAsCustomer}
              data-testid="admin-shop-as-customer"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">
              <ShoppingBag weight="duotone" size={20} /> Shop as customer
            </button>
            <button onClick={() => { logout(); nav("/seller/login"); }} data-testid="admin-logout"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50">
              <SignOut weight="duotone" size={20} /> Logout
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#F8CB46] flex items-center justify-center">
              <Lightning weight="fill" size={16} className="text-black" />
            </div>
            <span className="font-heading font-black">Shopkeeper</span>
          </div>
          <button onClick={() => { logout(); nav("/seller/login"); }} className="text-sm font-semibold text-red-500" data-testid="admin-mobile-logout">
            <SignOut size={20} />
          </button>
        </div>

        {/* Main */}
        <main className="flex-1 min-w-0 lg:pt-0 pt-14 pb-20 lg:pb-8">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-100 px-2 py-2 flex justify-around">
          {mobileItems.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 ${isActive ? "text-black" : "text-gray-500"}`
              }
              data-testid={`admin-bottom-${it.label.toLowerCase()}`}>
              <it.icon weight={"duotone"} size={20} />
              <span className="text-[10px] font-bold">{it.label}</span>
            </NavLink>
          ))}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="flex flex-col items-center gap-0.5 px-2 py-1 text-gray-500"
                data-testid="admin-bottom-more">
                <DotsThree weight="bold" size={22} />
                <span className="text-[10px] font-bold">More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl pb-8">
              <SheetHeader className="text-left mb-2">
                <SheetTitle className="font-heading font-black tracking-tight">More options</SheetTitle>
              </SheetHeader>
              <div className="grid grid-cols-3 gap-3">
                {items.filter((it) => mobileMoreLabels.has(it.label)).map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) =>
                      `flex flex-col items-center gap-1.5 p-3 rounded-xl border ${
                        isActive ? "border-[#F8CB46] bg-[#F8CB46]/10 text-black" : "border-gray-100 text-gray-700 hover:bg-gray-50"
                      }`
                    }
                    data-testid={`admin-more-${it.label.toLowerCase()}`}>
                    <it.icon weight="duotone" size={22} />
                    <span className="text-xs font-semibold">{it.label}</span>
                  </NavLink>
                ))}
                {storeSlug ? (
                  <a href={`/${storeSlug}`} target="_blank" rel="noopener noreferrer"
                    onClick={() => setMoreOpen(false)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-100 text-gray-700 hover:bg-gray-50"
                    data-testid="admin-more-storefront">
                    <Storefront weight="duotone" size={22} />
                    <span className="text-xs font-semibold">Storefront</span>
                  </a>
                ) : null}
                <button
                  type="button"
                  onClick={() => { setMoreOpen(false); shopAsCustomer(); }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-100 text-gray-700 hover:bg-gray-50"
                  data-testid="admin-more-shop-as-customer">
                  <ShoppingBag weight="duotone" size={22} />
                  <span className="text-xs font-semibold">Shop as customer</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setMoreOpen(false); logout(); nav("/seller/login"); }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-red-100 text-red-600 hover:bg-red-50"
                  data-testid="admin-more-logout">
                  <SignOut weight="duotone" size={22} />
                  <span className="text-xs font-semibold">Logout</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </div>
  );
}
